import {
  Navigate,
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from '@tanstack/react-router';
import { Flex, Spinner } from '@chakra-ui/react';
import { AppShell } from '@/widgets/app-shell/AppShell';
import { ClientShell } from '@/widgets/client-shell/ClientShell';
import { LoginPage } from '@/pages/LoginPage';
import { useAuthStore } from '@/shared/lib/auth/store';
import { PermissionGate } from '@/shared/ui/PermissionGate';
import { RouteErrorFallback } from '@/shared/ui/RouteErrorFallback';
import { lazyPage } from '@/shared/lib/lazy-page';
import { hasAnyPermissionIn, PERM, reachableRoutes, routeAccess } from '@/shared/lib/auth/access';
import { usePermissionCodes } from '@/shared/lib/auth/permissions';
import { applyManifestForPath } from '@/shared/lib/pwa/manifestLink';

/* Protected pages are code-split: each loads its own chunk on first
   navigation (preloaded on link hover via `defaultPreload: 'intent'`),
   so the initial bundle carries only the shell + login. The shell and
   login stay eager — they're needed for the very first paint.
   `lazyPage`, а не голый `lazyRouteComponent`: не скачавшийся файл страницы повторяется по
   адресу с меткой — Safari помнит упавший адрес за вкладкой (`shared/lib/lazy-page`). */

const DashboardPage = lazyPage(() => import('@/pages/DashboardPage'), 'DashboardPage');
const OrdersPage = lazyPage(() => import('@/pages/OrdersPage'), 'OrdersPage');
const OrderDetailsPage = lazyPage(() => import('@/pages/OrderDetailsPage'), 'OrderDetailsPage');
const MapPage = lazyPage(() => import('@/pages/MapPage'), 'MapPage');
const OperatorsPage = lazyPage(() => import('@/pages/OperatorsPage'), 'OperatorsPage');
const ProvidersPage = lazyPage(() => import('@/pages/ProvidersPage'), 'ProvidersPage');
const ClientsPage = lazyPage(() => import('@/pages/ClientsPage'), 'ClientsPage');
const CatalogPage = lazyPage(() => import('@/pages/CatalogPage'), 'CatalogPage');
const ConsumersPage = lazyPage(() => import('@/pages/ConsumersPage'), 'ConsumersPage');
const ConsumerZonesPage = lazyPage(() => import('@/pages/ConsumerZonesPage'), 'ConsumerZonesPage');
/* The drawing workspace is its own route rather than a tab of the table: a map needs the page, and
   `EntityCrudPage` has no slot for one. Lazy like every other route — the editor pulls in the map
   widget and its geometry, which nobody who never opens it should download. */
const ZoneMapPage = lazyPage(() => import('@/pages/ZoneMapPage'), 'ZoneMapPage');
const PricingPage = lazyPage(() => import('@/pages/PricingPage'), 'PricingPage');
const StuckDeliveriesPage = lazyPage(
  () => import('@/pages/StuckDeliveriesPage'),
  'StuckDeliveriesPage',
);
const PersonalCarSettingsPage = lazyPage(
  () => import('@/pages/PersonalCarSettingsPage'),
  'PersonalCarSettingsPage',
);
const ChatPage = lazyPage(() => import('@/pages/ChatPage'), 'ChatPage');
const RefillRequestsPage = lazyPage(
  () => import('@/pages/RefillRequestsPage'),
  'RefillRequestsPage',
);
const ReportsPage = lazyPage(() => import('@/pages/ReportsPage'), 'ReportsPage');
const ReportBuilderPage = lazyPage(() => import('@/pages/ReportBuilderPage'), 'ReportBuilderPage');
const AdminsPage = lazyPage(() => import('@/pages/AdminsPage'), 'AdminsPage');
const RolesPage = lazyPage(() => import('@/pages/RolesPage'), 'RolesPage');
const ClientReportBuilderPage = lazyPage(
  () => import('@/pages/ClientReportBuilderPage'),
  'ClientReportBuilderPage',
);
const ClientRefuelsPage = lazyPage(() => import('@/pages/ClientRefuelsPage'), 'ClientRefuelsPage');

function PendingFallback() {
  return (
    <Flex align="center" justify="center" p="10" color="fg.muted">
      <Spinner />
    </Flex>
  );
}

/* Code-based routes (no generated tree). All real pages live behind
   AppShell at /; /login is public and redirects authenticated users
   away from itself. Route-level redirects read the auth store
   directly — Zustand is sync, so this happens during navigation
   without a render flicker. */

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  beforeLoad: () => {
    const { status, user } = useAuthStore.getState();
    if (status === 'authenticated') {
      throw redirect({ to: user?.role === 'PROVIDER' ? '/client' : '/' });
    }
  },
  component: LoginPage,
});

const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_protected',
  beforeLoad: () => {
    const { status, user } = useAuthStore.getState();
    if (status === 'anonymous') throw redirect({ to: '/login' });
    // A provider account belongs in the client cabinet, not the admin shell.
    if (user?.role === 'PROVIDER') throw redirect({ to: '/client' });
  },
  component: AppShell,
});

const clientRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_client',
  beforeLoad: () => {
    const { status, user } = useAuthStore.getState();
    if (status === 'anonymous') throw redirect({ to: '/login' });
    // The cabinet is provider-only; anyone else bounces to the admin app.
    if (user?.role !== 'PROVIDER') throw redirect({ to: '/' });
  },
  component: ClientShell,
});

/* Главная — адрес после входа. Сотрудника, которому она закрыта, ведём на первую открытую ему
   страницу меню, а не на замок: роль «только чаты» или «только справочники» — обычное дело. */
function HomeRoute() {
  const codes = usePermissionCodes();
  if (hasAnyPermissionIn(codes, routeAccess('/'))) return <DashboardPage />;
  const first = reachableRoutes(codes)[0];
  if (first) return <Navigate to={first} replace />;
  return (
    <PermissionGate anyOf={routeAccess('/')}>
      <DashboardPage />
    </PermissionGate>
  );
}

const dashboardRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/',
  component: HomeRoute,
});

const ordersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/orders',
  // ГЛ-2/ГЛ-3: a dashboard KPI card deep-links here with a state filter (single value or a
  // comma-separated active-states list — see ACTIVE_ORDER_STATES_PARAM in features/orders/api.ts).
  // `completedOn`: the Главная weekly-completed chart deep-links here with a day + state=COMPLETED.
  // `consumerId`: «Заправки» on the B2C consumers page deep-links here for one account.
  validateSearch: (
    search: Record<string, unknown>,
  ): { state?: string; completedOn?: string; consumerId?: string } => ({
    state: typeof search.state === 'string' ? search.state : undefined,
    completedOn: typeof search.completedOn === 'string' ? search.completedOn : undefined,
    consumerId: typeof search.consumerId === 'string' ? search.consumerId : undefined,
  }),
  component: () => (
    <PermissionGate anyOf={routeAccess('/orders')}>
      <OrdersPage />
    </PermissionGate>
  ),
});

const orderDetailsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/orders/$orderId',
  component: () => (
    <PermissionGate anyOf={routeAccess('/orders')}>
      <OrderDetailsPage />
    </PermissionGate>
  ),
});

const mapRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/map',
  // КАРТА-8/9: "zoom to this order" navigates here with the target order's id so OrdersMap can
  // sharply pan/zoom to its marker on mount. ВОД-3: same deep-link shape for a driver, from the
  // "Водители" page.
  validateSearch: (search: Record<string, unknown>): { orderId?: string; driverId?: string } => ({
    orderId: typeof search.orderId === 'string' ? search.orderId : undefined,
    driverId: typeof search.driverId === 'string' ? search.driverId : undefined,
  }),
  component: () => (
    <PermissionGate anyOf={routeAccess('/map')}>
      <MapPage />
    </PermissionGate>
  ),
});

const operatorsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/operators',
  component: () => (
    <PermissionGate anyOf={routeAccess('/operators')}>
      <OperatorsPage />
    </PermissionGate>
  ),
});

const providersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/providers',
  component: () => (
    <PermissionGate anyOf={routeAccess('/providers')}>
      <ProvidersPage />
    </PermissionGate>
  ),
});

const clientsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/clients',
  component: () => (
    <PermissionGate anyOf={routeAccess('/clients')}>
      <ClientsPage />
    </PermissionGate>
  ),
});

const catalogRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/catalog',
  component: () => (
    <PermissionGate anyOf={routeAccess('/catalog')}>
      <CatalogPage />
    </PermissionGate>
  ),
});

// B2C consumer accounts (B11). `consumers.*` — свой раздел прав, не `clients.*`: те про кабинеты
// B2B-клиентов, другое понятие.
const consumersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/consumers',
  component: () => (
    <PermissionGate anyOf={routeAccess('/consumers')}>
      <ConsumersPage />
    </PermissionGate>
  ),
});

// B2C city provisioning — справочник «Зоны обслуживания» (`catalog.zones.*`).
const consumerZonesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/consumer-zones',
  component: () => (
    <PermissionGate anyOf={routeAccess('/consumer-zones')}>
      <ConsumerZonesPage />
    </PermissionGate>
  ),
});

/* Карта зон — мастерская, где пятна рисуют: открывается тем, кому зоны можно править. */
const zoneMapRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/consumer-zones/map',
  component: () => (
    <PermissionGate anyOf={[PERM.catalogZonesEdit]}>
      <ZoneMapPage />
    </PermissionGate>
  ),
});

const personalCarSettingsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/personal-car-settings',
  component: () => (
    <PermissionGate anyOf={routeAccess('/personal-car-settings')}>
      <PersonalCarSettingsPage />
    </PermissionGate>
  ),
});

/* Незавершённые операции — деньги заказов, своё право в разделе «Заказы». */
const stuckDeliveriesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/stuck-deliveries',
  component: () => (
    <PermissionGate anyOf={routeAccess('/stuck-deliveries')}>
      <StuckDeliveriesPage />
    </PermissionGate>
  ),
});

const pricingRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/pricing',
  component: () => (
    <PermissionGate anyOf={routeAccess('/pricing')}>
      <PricingPage />
    </PermissionGate>
  ),
});

const chatRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/chat',
  // `consumer`: «Написать клиенту» на странице заказа открывает переписку с этим клиентом.
  validateSearch: (search: Record<string, unknown>): { consumer?: string } => ({
    consumer: typeof search.consumer === 'string' ? search.consumer : undefined,
  }),
  component: () => (
    <PermissionGate anyOf={routeAccess('/chat')}>
      <ChatPage />
    </PermissionGate>
  ),
});

const refillRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/refill-requests',
  component: () => (
    <PermissionGate anyOf={routeAccess('/refill-requests')}>
      <RefillRequestsPage />
    </PermissionGate>
  ),
});

const reportsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/reports',
  component: () => (
    <PermissionGate anyOf={routeAccess('/reports')}>
      <ReportsPage />
    </PermissionGate>
  ),
});

const reportBuilderRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/reports/builder',
  component: () => (
    <PermissionGate anyOf={routeAccess('/reports/builder')}>
      <ReportBuilderPage />
    </PermissionGate>
  ),
});

const adminsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/admins',
  component: () => (
    <PermissionGate anyOf={routeAccess('/admins')}>
      <AdminsPage />
    </PermissionGate>
  ),
});

const adminRolesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/admin-roles',
  component: () => (
    <PermissionGate anyOf={routeAccess('/admin-roles')}>
      <RolesPage />
    </PermissionGate>
  ),
});

/* Two destinations. `/client` is the refuel history (the account's own refuels, with the photos
   and clips of each) — restored after the July rewrite left the cabinet reports-only while the
   backend kept serving `/v1/client/orders` and its media routes to nobody. `/client/reports` is
   the read-only list of reports an admin assembled and assigned to this client; the client-side
   report BUILDER stays gone, which is what that rewrite was actually about. */
const clientCabinetRoute = createRoute({
  getParentRoute: () => clientRoute,
  path: '/client',
  component: ClientRefuelsPage,
});

const clientReportsRoute = createRoute({
  getParentRoute: () => clientRoute,
  path: '/client/reports',
  component: ClientReportBuilderPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  protectedRoute.addChildren([
    dashboardRoute,
    ordersRoute,
    orderDetailsRoute,
    mapRoute,
    operatorsRoute,
    providersRoute,
    clientsRoute,
    catalogRoute,
    consumersRoute,
    consumerZonesRoute,
    zoneMapRoute,
    personalCarSettingsRoute,
    stuckDeliveriesRoute,
    pricingRoute,
    chatRoute,
    refillRoute,
    reportsRoute,
    reportBuilderRoute,
    adminsRoute,
    adminRolesRoute,
  ]),
  clientRoute.addChildren([clientCabinetRoute, clientReportsRoute]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPendingComponent: PendingFallback,
  defaultErrorComponent: RouteErrorFallback,
});

// `main.tsx` sets the manifest link once at cold load, from the URL the tab was opened with.
// A provider logging in on `/` and getting client-side redirected to `/client` never reloads, so
// without this the install prompt would still offer the admin manifest until the next hard load.
router.subscribe('onResolved', ({ toLocation }) => {
  applyManifestForPath(toLocation.pathname);
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
